/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';

// @mui material components
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import InputAdornment from '@mui/material/InputAdornment';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

import FormQuantity from './FormQuantity';
import SearchRequest from './SearchRequest';
import ExportStoreLot from './ExportStoreLot';

const initParent = {
  id: '',
  code: '',
  remark: '',
  status: 'edit',
  print_url: '',
  created_at: '',
  updated_at: '',
};

const initPhoto = {
  id: '',
  code: '',
  sku: '',
  name: '',
  image: '',
};

function ExportStoreList() {
  const mod = 'export_store_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadAdd, setLoadAdd] = useState(false);
  const [parent, setParent] = useState(initParent);
  const [quantity, setQuantity] = useState('');
  const [search, setSearch] = useState('');
  const [barcodeDisplay, setBarcodeDisplay] = useState('');
  const [textBarcode, setTextBarcode] = useState(false);
  const [selectProduct, setSelectProduct] = useState(false);
  const [quantityExport, setQuantityExport] = useState(0);
  const [modalPhoto, setModalPhoto] = useState(false);
  const [photo, setPhoto] = useState(initPhoto);
  const txtSearch = useRef(null);

  let barcodeScan = '';

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = () => {
    setLoading(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);
      }
      setLoading(false);
    });
  };

  const add = (barcode, qty) => {
    if (barcode === '') return;
    if (loadAdd) return;

    setLoadAdd(true);
    FetchData('PUT', `/${mod}`, app.token, {
      ref_id: id,
      barcode: barcode.split('Shift').join(''),
      quantity: qty === undefined ? quantity : qty,
    }).then((result) => {
      setLoadAdd(false);

      if (result.status === 'ok') {
        setSearch('');
        setQuantity('');

        setRecord(result.data);
        setParent(result.parent);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(result.msg, 'error');
      }
    });
  };

  const addBarcode = (barcode) => {
    if (parent.status !== 'edit') return;
    add(barcode);
  };

  const addText = () => {
    if (parent.status !== 'edit') return;
    add(search);
  };

  const addRequest = (item) => {
    add(item.barcode, item.quantity);
  };

  const edit = (item) => {
    setLoading(true);

    FetchData('PATCH', `/${mod}/${item.id}`, app.token, {
      estock_id: item.estock_id,
      stock_id: item.stock_id,
      lot: item.lot,
      is_expire: item.is_expire,
      date_expire: item.date_expire,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const del = (item) => {
    setLoading(true);

    FetchData('DELETE', `/${mod}/${item.id}`, app.token, {
      ref_id: id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);
        showNotify(t(`alert.deletecomplete`), 'success');
      } else {
        showNotify(t(`alert.deleteerror`), 'error');
      }
    });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setQuantityExport(result.quantity_export);
        view();
      } else {
        Func.exitApp();
      }
    });
  };

  const editQuantity = (input) => {
    setQuantity(input);

    if (textBarcode) {
      setTimeout(function () {
        txtSearch.current.focus();
      }, 500);
    }
  };

  const btnDel = (item) => {
    if (parent.status !== 'edit') return null;

    return (
      <MDButton
        variant='text'
        tabIndex={-1}
        color='error'
        size='small'
        iconOnly
        onClick={() => del(item)}
      >
        <Icon>delete</Icon>
      </MDButton>
    );
  };

  const activeDisplay = (inp) => {
    if (inp === 'Y') return <Icon color='success'>verified</Icon>;
    if (inp === 'N') return <Icon color='error'>highlight_off</Icon>;
    return <Icon color='warning'>hourglass_top</Icon>;
  };

  const renderListView = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
              <Table aria-label='datatable table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' width={10}>
                      No.
                    </TableCell>
                    <TableCell align='center' width={40}>
                      {t(`attr.image`)}
                    </TableCell>
                    <TableCell>สินค้า</TableCell>
                    <TableCell align='center'>LOT</TableCell>
                    <TableCell align='center'>วันหมดอายุ</TableCell>
                    <TableCell align='right' width={120}>
                      จำนวน
                    </TableCell>
                    <TableCell align='right' width={120}>
                      รับเข้า
                    </TableCell>
                    <TableCell align='right' width={120}>
                      ค้างโอน
                    </TableCell>
                    <TableCell>หน่วย</TableCell>
                    <TableCell align='center' width={10}>
                      ตรวจ
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row, i) => (
                    <TableRow key={row.id}>
                      <TableCell align='center'>{i + 1}</TableCell>
                      <TableCell
                        align='center'
                        onClick={() => {
                          setPhoto(row);
                          setModalPhoto(true);
                        }}
                      >
                        {Func.imageDisplay(row)}
                      </TableCell>
                      <TableCell>
                        <MDBox>{row.barcode}</MDBox>
                        <MDBox>{row.name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>{row.lot}</TableCell>
                      <TableCell align='center'>
                        {row.is_expire === 'Y' ? Func.dateDisplay(row.date_expire) : 'N/A'}
                      </TableCell>
                      <TableCell align='right' className='color-primary'>
                        {Func.numberDisplay(row.quantity)}
                      </TableCell>
                      <TableCell align='right' className='color-success'>
                        {Func.numberDisplay(row.quantity_receive)}
                      </TableCell>
                      <TableCell align='right' className='color-danger'>
                        {Func.numberDisplay(row.quantity_balance)}
                      </TableCell>
                      <TableCell align='left'>{row.unit_name}</TableCell>
                      <TableCell align='center'>{activeDisplay(row.active)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDBox p={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={5}>
                  {textBarcode ? (
                    <MDBox
                      noValidate
                      component='form'
                      role='form'
                      onSubmit={(e) => {
                        e.preventDefault();
                        addText();
                      }}
                    >
                      <MDInput
                        name='search'
                        type='text'
                        fullWidth
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ input: { color: '#000', fontSize: '15px' } }}
                        value={search}
                        InputProps={{
                          inputRef: txtSearch,
                          maxLength: 50,
                          autoFocus: true,
                          inputProps: {
                            autoComplete: 'off',
                          },
                          startAdornment: (
                            <InputAdornment position='start'>
                              <MDTypography variant='body' color='dark' sx={{ width: 65 }}>
                                BARCODE :
                              </MDTypography>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position='end'>
                              <MDButton
                                variant='contained'
                                color='dark'
                                size='small'
                                iconOnly
                                onClick={() => {
                                  setTextBarcode(false);
                                  setSearch('');
                                }}
                              >
                                <Icon>reply</Icon>
                              </MDButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </MDBox>
                  ) : (
                    <MDButton
                      variant='outlined'
                      color='dark'
                      size='large'
                      fullWidth
                      sx={{ display: 'inline-block', textAlign: 'left' }}
                      onClick={() => {
                        setTextBarcode(true);
                        setTimeout(function () {
                          txtSearch.current.focus();
                        }, 500);
                      }}
                    >
                      Barcode : {barcodeDisplay}
                    </MDButton>
                  )}
                </Grid>
                <Grid item xs={12} sm={1}>
                  <FormQuantity
                    quantityExport={quantityExport}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    callback={editQuantity}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox display='flex' justifyContent='flex-end' gap={1}>
                    <SearchRequest
                      add={addRequest}
                      refresh={view}
                      setSelectProduct={setSelectProduct}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
              <Table aria-label='simple table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' width={10}>
                      No.
                    </TableCell>
                    <TableCell align='center' width={40}>
                      {t(`attr.image`)}
                    </TableCell>
                    <TableCell>สินค้า</TableCell>
                    <TableCell align='center'>LOT</TableCell>
                    <TableCell align='center'>วันหมดอายุ</TableCell>
                    <TableCell align='right' width={120}>
                      จำนวน
                    </TableCell>
                    <TableCell width={100}>หน่วย</TableCell>
                    <TableCell width={10}>&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row, i) => (
                    <TableRow key={row.id}>
                      <TableCell align='center'>{i + 1}</TableCell>
                      <TableCell align='center'>{Func.imageDisplay(row)}</TableCell>
                      <TableCell>
                        <MDBox>{row.barcode}</MDBox>
                        <MDBox>{row.name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        {row.count_lot > 1 ? <ExportStoreLot parent={row} edit={edit} /> : row.lot}
                      </TableCell>
                      <TableCell align='center'>
                        {row.is_expire === 'Y' ? Func.dateDisplay(row.date_expire) : 'N/A'}
                      </TableCell>
                      <TableCell align='right' className='color-primary'>
                        {Func.numberDisplay(row.quantity)}
                      </TableCell>
                      <TableCell align='left'>{row.unit_name}</TableCell>
                      <TableCell align='center'>{btnDel(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {photo.code} <span className='color-default'>{photo.sku}</span>
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={1}>
            <MDTypography variant='body'>{photo.name}</MDTypography>
          </MDBox>
          <MDBox
            component='img'
            src={photo.image}
            alt=''
            borderRadius='lg'
            width='100%'
            position='relative'
            sx={{ boxShadow: 0 }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  useHotkeys('f2', () => setTextBarcode(true));

  const handleKeyDown = (e) => {
    if (!selectProduct) {
      if (e.keyCode === 13 && barcodeScan.length > 4) {
        setBarcodeDisplay(barcodeScan);
        addBarcode(barcodeScan);
        return;
      }
    }

    barcodeScan += e.key;

    setTimeout(() => {
      barcodeScan = '';
      setBarcodeDisplay(barcodeScan);
    }, 500);
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return function cleanup() {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [quantity, selectProduct]);

  return (
    <div>
      {parent.status === 'edit' ? renderList() : renderListView()}
      {renderPhoto()}
      {notify}
    </div>
  );
}

export default ExportStoreList;
